import { Dialog, DialogProps } from "@flat-ui";
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useRef,
  useState,
} from "react";

export type CreateSphereDialogProps = Omit<
  DialogProps,
  "open" | "onConfirm" | "onClose" | "onCancel"
> & {
  isHideConfirm?: boolean;
  isHideClose?: boolean;
  isHideCancel?: boolean;
};

type SphereDialogData = {
  /**
   * @returns a promise with a boolean value, which is true if the dialog has been confirmed and false otherwise
   * @param content - The content of the dialog
   * @param options - Object which sets the possible customization for the dialog
   */
  createDialog(
    options: CreateSphereDialogProps,
    content?: JSX.Element | string
  ): Promise<boolean>;

  /** Closes the open dialog, if any, optionally canceling or confirming it. */
  closeDialog(shouldConfirm?: boolean): void;
};

/** A dialog context which provides the components with the function to create a dialog */
const DialogContext = createContext<SphereDialogData | undefined>(undefined);

/**
 *  @returns A dialog provider which allows the components to create a dialog from anywhere
 */
export function SphereDialogProvider({
  children,
}: PropsWithChildren<unknown>): JSX.Element {
  const confirmPromiseRef = useRef<
    | {
        resolve(value: boolean): void;
      }
    | undefined
  >(undefined);

  const [dialogOptions, setDialogOptions] =
    useState<CreateSphereDialogProps | null>(null);
  const [content, setContent] = useState<JSX.Element | string>("");

  /**
   * @returns a promise with a boolean value, which is true if the dialog has been confirmed and false otherwise
   * @param options - object which sets the possible customization for the dialog
   */
  const createDialog = useCallback(
    async (
      options: CreateSphereDialogProps,
      content?: JSX.Element
    ): Promise<boolean> => {
      setDialogOptions(options);
      setContent(content ?? "");
      return new Promise((resolve) => {
        confirmPromiseRef.current = { resolve };
      });
    },
    []
  );

  const closeDialog = useCallback((shouldConfirm?: boolean) => {
    if (shouldConfirm !== undefined && confirmPromiseRef.current) {
      confirmPromiseRef.current.resolve(shouldConfirm);
    }
    setDialogOptions(null);
  }, []);

  function handleCancel(): void {
    if (confirmPromiseRef.current) {
      confirmPromiseRef.current.resolve(false);
    }
    setDialogOptions(null);
  }

  function handleConfirm(): void {
    if (confirmPromiseRef.current) {
      confirmPromiseRef.current.resolve(true);
    }
    setDialogOptions(null);
  }

  return (
    <DialogContext.Provider value={{ createDialog, closeDialog }}>
      {dialogOptions && (
        <Dialog
          open={!!dialogOptions}
          onConfirm={dialogOptions?.isHideConfirm ? undefined : handleConfirm}
          onClose={dialogOptions?.isHideClose ? undefined : handleCancel}
          onCancel={dialogOptions?.isHideCancel ? undefined : handleCancel}
          {...dialogOptions}
        >
          {content}
        </Dialog>
      )}
      {children}
    </DialogContext.Provider>
  );
}

/**
 * @returns Hook that returns the utility function to create a dialog
 */
export function useSphereDialog(): SphereDialogData {
  const ctx = useContext(DialogContext);
  if (!ctx) {
    throw Error("useSphereDialog called outside the SphereDialogContext");
  }
  return ctx;
}
